import React, { useEffect, useState, useRef } from 'react';
import * as echarts from 'echarts';
import { Empty } from 'antd';
import { useNavigate } from 'react-router-dom';
import mainDashboardApis from '../../api/dashboard/MainDashboard';
import PRSummaryCategoryDrawer from './PRSummaryCategoryDrawer';

interface StatusData {
    name: string;
    value: number;
    categoryId?: string;
    id?: string | number;
}

interface PRSummaryWidgetProps {
    prSummaryData: any[];
    startDate?: string;
    endDate?: string;
    siteId?: string;
}

const PRSummaryWidget: React.FC<PRSummaryWidgetProps> = ({prSummaryData, startDate, endDate, siteId}) => {
    const navigate = useNavigate();
    const [drawerVisible, setDrawerVisible] = useState(false);
    const [selectedCategoryId, setSelectedCategoryId] = useState<string>("");
    const chartInstanceRef = useRef<echarts.ECharts | null>(null);
    const data: StatusData[] = prSummaryData?.filter((item: any) => item.value !== "0");
    // Check if all values are 0 or if prSummaryData is undefined
    const allValuesZero = !prSummaryData || prSummaryData.every((item: any) => item.value === "0");


    useEffect(() => {
        if (allValuesZero) return; // Skip initializing the chart if all values are 0

        const chartDom = document.getElementById('pr-summary-widget') as HTMLElement;
        if (!chartDom) return;

        const myChart = echarts.init(chartDom);
        chartInstanceRef.current = myChart;

        const option = {
            tooltip: {
                trigger: 'item',
            },
            series: [
                {
                    name: 'PR Status',
                    type: 'pie',
                    radius: ['50%', '70%'],
                    avoidLabelOverlap: false,
                    padAngle: 2,

                    label: {
                        show: true,
                        formatter: '{b|{b}}\n{c|{c} ({d}%)}',
                        rich: {
                            b: {
                                fontSize: 14,
                                color: '#333',
                                lineHeight: 24,
                            },
                            c: {
                                fontSize: 12,
                                color: '#999',
                                lineHeight: 20,
                            },
                        },
                    },
                    labelLine: {
                        show: true,
                        length: 15,
                        length2: 10,
                        smooth: true,
                    },
                    itemStyle: {
                        borderColor: '#E0E4FF',  // Border color
                        borderWidth: 1,           // Border width
                        shadowBlur: 10,           // Shadow blur
                        shadowOffsetX: 0,         // Shadow X offset
                        shadowOffsetY: 5,         // Shadow Y offset
                        shadowColor: 'rgba(255, 255, 255, 0.2)', // Shadow color
                        borderRadius: 5
                    },
                    data: data?.map((item) => ({ 
                        value: item.value, 
                        name: item.name,
                        categoryId: item.categoryId || item.id || item.name
                    })),
                },
            ],
            color : ['#173F5F', '#20639B', '#3CAEA3', '#F6D55C', '#ED553B', '#e81e63', '#03a9f4', '#cddc39', '#9c27b0', '#ffeb3b'],
        };
        myChart.setOption(option);

        // Add click event handler - only navigate for "open" status
        myChart.on('click', (params: any) => {
            const statusName = params.name || params.data?.name || "";
            // Only navigate for "open" status (case-insensitive)
            if (statusName.toLowerCase() === "open") {
                // Navigate to details page like "View Details" button
                if (startDate && endDate) {
                    navigate('/pr-summary-details', {
                        state: {
                            startDate: startDate,
                            endDate: endDate,
                        }
                    });
                }
            }
            // For all other statuses, do nothing (no navigation, no drawer)
        });

        const handleWindowResize = () => myChart.resize();
        window.addEventListener('resize', handleWindowResize);

        const resizeObserver = new ResizeObserver(() => {
            myChart.resize();
        });
        resizeObserver.observe(chartDom);

        return () => {
            window.removeEventListener('resize', handleWindowResize);
            resizeObserver.disconnect();
            myChart.dispose();
            chartInstanceRef.current = null;
        };
    }, [data]);

    const handleViewClick = () => {
        if (!startDate || !endDate) {
            console.error("Start date and end date are required");
            return;
        }
        // Navigate to the details page with date range in state
        navigate('/pr-summary-details', {
            state: {
                startDate: startDate,
                endDate: endDate,
            }
        });
    };

    return (
        <>
            <div className="card-title-group mb-1" style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
            }}>
                <div className="card-title">
                    <h6 className="title">Purchase Request Summary</h6>
                    <p>
                        Tracking the Journey from Request to Delivery
                    </p>
                </div>
                <a
                    href="#"
                    style={{
                        marginLeft: 'auto',
                        textAlign: 'right',
                        cursor: 'pointer',
                    }}
                    onClick={(event) => {
                        event.preventDefault();
                        handleViewClick();
                    }}
                >
                    View Details
                </a>
            </div>
             {/* Conditionally render the chart or the empty state */}
             {allValuesZero ? (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
                    <Empty description="No Data Available " />
                </div>
            ) : (
                <div id="pr-summary-widget" style={{ width: '100%', height: '300px' }} />
            )}
            <PRSummaryCategoryDrawer
                visible={drawerVisible}
                onClose={() => {
                    setDrawerVisible(false);
                    setSelectedCategoryId("");
                }}
                categoryId={selectedCategoryId}
                siteId={siteId}
                startDate={startDate}
                endDate={endDate}
            />
        </>
    )
};

export default PRSummaryWidget;
